import type { AuthSessionPayload } from "site/services/auth-session.types";
import { decodeSessionWire, wireToAuthSessionPayload } from "site/services/session-wire";
import { toBufferSource } from "site/utils";

export const ENVELOPE_PURPOSE_VERIFY = "verify";
export const ENVELOPE_PURPOSE_REFRESH = "refresh";

async function deriveEnvelopeKey(purpose: string, secret: string): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const parts = [enc.encode("xq-session-envelope-v1\x00"), enc.encode(purpose), new Uint8Array([0]), enc.encode(secret)];
  let total = 0;
  for (const p of parts) {
    total += p.length;
  }
  const buf = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    buf.set(p, off);
    off += p.length;
  }
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return new Uint8Array(hash);
}

export async function openSessionEnvelope(
  blob: ArrayBuffer,
  purpose: string,
  secret: string,
): Promise<AuthSessionPayload> {
  const raw = new Uint8Array(blob);
  if (raw.length < 12 + 16) {
    throw new Error("session envelope: too short");
  }
  const keyBytes = await deriveEnvelopeKey(purpose, secret);
  const cryptoKey = await crypto.subtle.importKey("raw", toBufferSource(keyBytes), { name: "AES-GCM" }, false, ["decrypt"]);
  const nonce = raw.slice(0, 12);
  const ciphertext = raw.slice(12);
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: toBufferSource(nonce) }, cryptoKey, toBufferSource(ciphertext));
  const wire = decodeSessionWire(new Uint8Array(plain));
  return wireToAuthSessionPayload(wire);
}

export function verifyEnvelopeSecret(email: string, code: string): string {
  return `${email.trim().toLowerCase()}\x00${code.trim()}`;
}
